import { useAccount, useMsal } from "@azure/msal-react";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import type { ChatVectorIndexOption, DataSourceWithIndexDescription, RawDatasource } from "src/db";
import { getAPI } from "src/utils/fetch";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

function flattenDatasources(raw: RawDatasource[]): ChatVectorIndexOption[] {
    return raw.flatMap((ds) => {
        if (ds.index.length === 0) {
            return [
                {
                    label: ds.label,
                    id: ds.id,
                    datasource_id: ds.id,
                    datasource_label: ds.label,
                    index_name: "",
                    enable_chat_with_document: ds.enable_chat_with_document,
                    index_description: [],
                },
            ];
        }
        return ds.index.map((idx) => ({
            label: idx.label,
            id: `${ds.id}/${idx.name}`,
            datasource_id: ds.id,
            datasource_label: ds.label,
            index_name: idx.name,
            enable_chat_with_document: ds.enable_chat_with_document,
            index_description: idx.index_description,
        }));
    });
}

export async function fetchDatasources(instance): Promise<ChatVectorIndexOption[]> {
    const response = await getAPI({
        url: `${BACKEND_URL}/v1/search/datasources`,
        instance,
    });
    const raw: RawDatasource[] = await response.json();
    const data = flattenDatasources(raw);
    localStorage.setItem("datasourceDetails", JSON.stringify(data));
    return data;
}

export const useDatasources = (): UseQueryResult<ChatVectorIndexOption[], Error> => {
    const { instance, inProgress } = useMsal();
    const account = useAccount();
    const isAuthenticated = !!account?.idToken;

    return useQuery<ChatVectorIndexOption[], Error>({
        queryKey: ["datasources"],
        queryFn: () => fetchDatasources(instance),
        enabled: isAuthenticated && inProgress === "none",
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });
};

export const useColumns = (
    index: string,
): UseQueryResult<DataSourceWithIndexDescription, Error> => {
    const { instance, inProgress } = useMsal();
    const account = useAccount();
    const isAuthenticated = !!account?.idToken;

    return useQuery<DataSourceWithIndexDescription, Error>({
        queryKey: ["datasources", index],
        queryFn: async () => {
            const localSources = localStorage.getItem("datasourceDetails");
            const sources: ChatVectorIndexOption[] =
                localSources === null ? await fetchDatasources(instance) : JSON.parse(localSources);
            const source = sources?.find((o) => o.id === index);

            return {
                index_description: source.index_description,
                namespace: source.index_name,
            };
        },
        enabled: isAuthenticated && inProgress === "none",
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });
};
